import { useCallback, useEffect, useState } from "react";
import { api, apiError, eur } from "@/lib/api";
import { useI18n } from "@/i18n";
import { toast } from "sonner";
import { Th, Td, NumberInput } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Truck } from "lucide-react";

const EMPTY = { cost_net: "", tax_code: "STANDARD", cost_status: "final", carrier: "",
  shipping_reference: "", carrier_invoice_reference: "", occurred_at: "", reason: "" };

/** Versandkosten eines Auftrags: dem Kunden berechneter Versand (bereits im Umsatz enthalten)
 *  und die tatsächlichen Carrier-Kosten je Sendung. Fachlich strikt getrennt. */
export default function ShippingCostCard({ orderId, canEdit, reloadKey }) {
  const { t, fmtDate } = useI18n();
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState(null); // Sendung, die erfasst/korrigiert wird
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState(null);
  const [carriers, setCarriers] = useState([]);

  const load = useCallback(() => {
    api.get(`/orders/${orderId}/shipping-costs`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [orderId]);
  useEffect(() => { load(); }, [load, reloadKey]);
  useEffect(() => { api.get("/carriers").then((r) => setCarriers(r.data)).catch(() => {}); }, []);

  if (!data || data.fulfillments.length === 0) return null;

  const openEdit = (ff) => {
    const sc = ff.shipping_cost;
    setEdit(ff);
    setF(sc ? { cost_net: String(sc.cost_net), tax_code: sc.tax_code || "STANDARD",
      cost_status: sc.cost_status, carrier: sc.carrier || "", shipping_reference: sc.shipping_reference || "",
      carrier_invoice_reference: sc.carrier_invoice_reference || "",
      occurred_at: sc.occurred_at || "", reason: "" } : { ...EMPTY, carrier: ff.carrier || "" });
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/fulfillments/${edit.id}/shipping-cost`, {
        // Kein stiller 0-Fallback: "unbekannt" bleibt unbekannt, 0,00 muss bewusst eingegeben werden.
        cost_net: Number(f.cost_net), tax_code: f.tax_code, cost_status: f.cost_status,
        carrier: f.carrier, shipping_reference: f.shipping_reference,
        carrier_invoice_reference: f.carrier_invoice_reference,
        occurred_at: f.occurred_at || null, reason: f.reason });
      toast.success(t("ship.saved"));
      setEdit(null); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const showHistory = async (ff) => {
    const r = await api.get(`/fulfillments/${ff.id}/shipping-cost/history`);
    setHistory({ id: ff.id, entries: r.data.entries || [] });
  };

  const tot = data.actual_total;
  const statusLabel = (s) => (s ? t(`ship.status.${s}`, s) : "—");

  return (
    <div className="rounded-md border border-border bg-card shadow-sm overflow-hidden" data-testid="shipping-cost-card">
      <div className="px-4 py-3 border-b border-border font-heading font-medium flex items-center gap-2">
        <Truck className="h-4 w-4" /> {t("ship.title")}
      </div>
      <div className="p-4 space-y-3">
        <p className="text-xs text-muted-foreground" data-testid="shipping-customer-note">
          {t("ship.customerChargeNote")}
        </p>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="bg-muted/50"><tr>
              <Th>{t("ship.shipment")}</Th><Th>{t("ship.carrier")}</Th><Th>{t("ship.reference")}</Th>
              <Th right>{t("common.net")}</Th><Th right>{t("invc.taxAmount")}</Th><Th right>{t("common.gross")}</Th>
              <Th>{t("common.status")}</Th><Th right></Th>
            </tr></thead>
            <tbody data-testid="shipping-cost-table">
              {data.fulfillments.map((ff, i) => {
                const sc = ff.shipping_cost;
                return (
                  <tr key={ff.id}>
                    <Td>{i + 1}{ff.shipped_at ? ` · ${fmtDate(ff.shipped_at)}` : ""}</Td>
                    <Td>{sc?.carrier || ff.carrier || "—"}</Td>
                    <Td mono>
                      {sc?.shipping_reference || "—"}
                      {sc?.carrier_invoice_reference
                        ? <span className="block text-xs text-muted-foreground">{sc.carrier_invoice_reference}</span>
                        : null}
                    </Td>
                    <Td right mono data-testid={`ship-net-${i}`}>{sc ? eur(sc.cost_net) : t("ship.open")}</Td>
                    <Td right mono>{sc ? `${eur(sc.tax_amount)} (${sc.tax_rate}%)` : "—"}</Td>
                    <Td right mono>{sc ? eur(sc.cost_gross) : "—"}</Td>
                    <Td data-testid={`ship-status-${i}`}>{statusLabel(sc?.cost_status)}</Td>
                    <Td right>
                      <div className="flex justify-end gap-1">
                        {sc && <Button size="sm" variant="ghost" onClick={() => showHistory(ff)} data-testid={`ship-history-${i}`}>{t("ship.history")}</Button>}
                        {canEdit && <Button size="sm" variant="outline" onClick={() => openEdit(ff)} data-testid={`ship-edit-${i}`}>
                          {sc ? t("ship.correct") : t("ship.capture")}</Button>}
                      </div>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot><tr className="border-t-2 border-border bg-muted/30 font-medium">
              <Td colSpan={3}>{tot.missing > 0 ? t("ship.capturedSoFar") : t("ship.actualTotal")}</Td>
              <Td right mono data-testid="ship-total-net">{eur(tot.net)}</Td>
              <Td right mono>{eur(tot.tax)}</Td>
              <Td right mono data-testid="ship-total-gross">{eur(tot.gross)}</Td>
              <Td data-testid="ship-total-status">{statusLabel(tot.status)}</Td><Td></Td>
            </tr></tfoot>
          </table>
        </div>
        {tot.missing > 0 && (
          <p className="text-xs text-chart-3" data-testid="shipping-missing-hint">
            {t("ship.notFinalHint", { count: tot.missing })}
          </p>
        )}
      </div>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{t("ship.dialogTitle")}</DialogTitle></DialogHeader>
          <DialogDescription>{t("ship.dialogHint")}</DialogDescription>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>{t("ship.costNet")}</Label>
              <NumberInput decimals={2} value={f.cost_net} className="mt-1.5" testId="ship-cost-net"
                onChange={(val) => setF({ ...f, cost_net: val })} /></div>
            <div><Label>{t("common.taxRate")}</Label>
              <select value={f.tax_code} onChange={(e) => setF({ ...f, tax_code: e.target.value })}
                data-testid="ship-tax-code"
                className="mt-1.5 h-10 w-full rounded-md border border-input bg-background px-2 text-sm">
                <option value="STANDARD">{t("tax.code.STANDARD", "STANDARD")}</option>
                <option value="REDUCED">{t("tax.code.REDUCED", "REDUCED")}</option>
                <option value="ZERO_RATE">{t("tax.code.ZERO_RATE", "ZERO_RATE")}</option>
              </select></div>
            <div><Label>{t("ship.carrier")}</Label>
              <select value={f.carrier} onChange={(e) => setF({ ...f, carrier: e.target.value })}
                className="mt-1.5 h-10 w-full rounded-md border border-input bg-background px-2 text-sm"
                data-testid="shipcost-carrier">
                <option value="">—</option>
                {carriers.filter((c) => c.active !== false || c.name === f.carrier)
                  .map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select></div>
            <div><Label>{t("ship.reference")}</Label>
              <Input value={f.shipping_reference} onChange={(e) => setF({ ...f, shipping_reference: e.target.value })}
                className="mt-1.5" data-testid="ship-reference" /></div>
            <div><Label>{t("ship.carrierInvoiceRef")} ({t("common.optional")})</Label>
              <Input value={f.carrier_invoice_reference}
                onChange={(e) => setF({ ...f, carrier_invoice_reference: e.target.value })}
                className="mt-1.5" data-testid="ship-carrier-invoice-ref" /></div>
            <div><Label>{t("ship.documentDate")}</Label>
              <Input type="date" value={f.occurred_at} onChange={(e) => setF({ ...f, occurred_at: e.target.value })}
                className="mt-1.5" data-testid="ship-date" /></div>
            <div><Label>{t("common.status")}</Label>
              <select value={f.cost_status} onChange={(e) => setF({ ...f, cost_status: e.target.value })}
                data-testid="ship-cost-status"
                className="mt-1.5 h-10 w-full rounded-md border border-input bg-background px-2 text-sm">
                <option value="provisional">{t("ship.status.provisional")}</option>
                <option value="final">{t("ship.status.final")}</option>
              </select></div>
            <div className="col-span-2"><Label>{t("common.reason")} ({t("common.optional")})</Label>
              <Input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })}
                className="mt-1.5" data-testid="ship-reason" /></div>
          </div>
          <DialogFooter>
            <Button onClick={save} data-testid="ship-save-btn"
              disabled={busy || f.cost_net === "" || Number.isNaN(Number(f.cost_net))}>
              {busy ? t("common.saving") : t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!history} onOpenChange={(o) => !o && setHistory(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{t("ship.historyTitle")}</DialogTitle></DialogHeader>
          <DialogDescription>{t("ship.historyHint")}</DialogDescription>
          <table className="w-full text-sm" data-testid="ship-history-table">
            <thead className="bg-muted/50"><tr><Th>{t("common.date")}</Th><Th right>{t("ship.deltaNet")}</Th>
              <Th>{t("common.status")}</Th><Th>{t("common.reason")}</Th><Th>{t("inv.by")}</Th></tr></thead>
            <tbody>
              {(history?.entries || []).map((e) => (
                <tr key={e.id}>
                  <Td>{fmtDate(e.created_at)}</Td>
                  <Td right mono>{eur(-e.amount)}</Td>
                  <Td>{statusLabel(e.cost_status)}</Td>
                  <Td className="text-xs">{e.reason || "—"}</Td>
                  <Td className="text-xs">{e.created_by_email || "—"}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </DialogContent>
      </Dialog>
    </div>
  );
}
